import React from 'react';
import { Controller, ControllerRenderProps, useFormContext } from 'react-hook-form';
import { SxProps, Theme, useTheme } from '@mui/material/styles';

import { Stack, Typography } from 'common/components/material';
import { ButtonTabs } from 'common/components/navigation';
import { FieldName } from 'common/types/Form.type';

type Option = {
  value: string;
  label: string;
};

type FormButtonTabsProps = {
  name: string;
  items: Option[];
  onChange?: (value: string) => void;
  sx?: SxProps<Theme>;
};

/**
 * Single-select segmented control bound to react-hook-form, for the handful of fields with
 * few enough options to show them all at once. Must be placed inside a FormProvider.
 */
const FormButtonTabs: React.FC<FormButtonTabsProps> = ({ name, items, onChange, sx }) => {
  const { palette } = useTheme();
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const errorMessage = errors[name]?.message as string | undefined;

  const renderTabs = ({ field }: { field: ControllerRenderProps<any, string> }) => {
    const handleChange = (value: string) => {
      field.onChange(value);
      onChange?.(value);
    };

    return (
      <ButtonTabs
        tabs={items}
        selectedValues={field.value ? [field.value] : []}
        onChange={handleChange}
        sx={{ gap: 1, flexWrap: 'wrap', ...sx }}
      />
    );
  };

  return (
    <Stack spacing={0.5}>
      <Controller control={control} name={name as FieldName} render={renderTabs} />
      {errorMessage && (
        <Typography variant="caption" color={palette.error.main}>
          {errorMessage}
        </Typography>
      )}
    </Stack>
  );
};

export default FormButtonTabs;
